import { useQuery } from '@tanstack/react-query'
import type { ClientSlug, Conversation, Platform, UserContext } from '@/types'
import { apiGet } from '@/lib/api'
import {
  apiConversationToConversation,
  apiMessageToFrontend,
  auditReplyToMessage,
  type ApiConversation,
  type ApiIncomingMessage,
  type ApiAuditHumanReply,
} from '@/lib/adapters'
import type { Message } from '@/types'
import { useCurrentUser } from './use-current-user'

export interface ConversationFilters {
  state?: 'bot' | 'human' | 'escalated' | 'closed' | 'all'
  client?: ClientSlug | 'all'
  platform?: Platform | 'all'
  search?: string
  slaOverdueOnly?: boolean
  /** Filtro por tipo del último mensaje del thread. 'all' o vacío = sin filtro. */
  messageKind?: 'dm' | 'comment' | 'all'
}

// state mapping: front → backend (cm_conversations.state enum)
const STATE_FRONT_TO_API: Record<string, 'bot' | 'human_taken' | 'escalated' | 'closed'> = {
  bot: 'bot',
  human: 'human_taken',
  escalated: 'escalated',
  closed: 'closed',
}

async function fetchConversationsForClient(
  clientSlug: ClientSlug,
  apiState?: string,
): Promise<Conversation[]> {
  const qs = new URLSearchParams({ clientSlug, limit: '50' })
  if (apiState) qs.set('state', apiState)
  const res = await apiGet<{ items: ApiConversation[]; nextCursor: string | null }>(
    `/conversations?${qs.toString()}`,
  )
  return res.items.map((c) => apiConversationToConversation(c))
}

export function useConversations(filters: ConversationFilters = {}) {
  const { data: user } = useCurrentUser()
  const apiState =
    filters.state && filters.state !== 'all' ? STATE_FRONT_TO_API[filters.state] : undefined

  return useQuery<Conversation[]>({
    queryKey: ['conversations', filters, user?.clientAccess],
    enabled: !!user,
    refetchInterval: 30_000,
    queryFn: async () => {
      if (!user) return []

      // Slugs a consultar.
      let slugs: ClientSlug[]
      if (filters.client && filters.client !== 'all') {
        if (user.role !== 'admin' && !user.clientAccess.includes(filters.client)) {
          return [] // sin acceso
        }
        slugs = [filters.client]
      } else {
        // 'all' o sin filtro → fan-out por todos los slugs del user.
        slugs = user.clientAccess
      }

      const perClient = await Promise.all(
        slugs.map((slug) =>
          fetchConversationsForClient(slug, apiState).catch(() => [] as Conversation[]),
        ),
      )
      let list = perClient.flat()

      // Filtros adicionales client-side (la API ya filtra por state+client).
      // NOTA: `messageKind` se aplica en el componente que renderiza la lista
      // (no acá) para poder mostrar contadores por tab sin re-fetchear.
      if (filters.platform && filters.platform !== 'all') {
        list = list.filter((c) => c.platform === filters.platform)
      }
      if (filters.slaOverdueOnly) {
        list = list.filter((c) => c.slaRemainingSec < 0)
      }
      if (filters.search) {
        const q = filters.search.toLowerCase()
        list = list.filter(
          (c) =>
            c.externalUserName.toLowerCase().includes(q) ||
            c.lastMessagePreview.toLowerCase().includes(q),
        )
      }

      // Orden: más nuevo primero (lastMessageAt DESC).
      // Antes ordenábamos por SLA restante ascendente — privilegiaba lo
      // más vencido — pero feedback del piloto pidió cronológico (más
      // reciente arriba). El SLA sigue visible en cada card; los vencidos
      // se filtran con el chip "SLA vencido" cuando hace falta.
      return list.sort(
        (a, b) =>
          new Date(b.lastMessageAt).getTime() -
          new Date(a.lastMessageAt).getTime(),
      )
    },
  })
}

/**
 * Devuelve una conversación + sus mensajes desde /api/conversations/:slug/:userId.
 *
 * IMPORTANTE: el id que recibe acá es el `id` de la conversación (uuid). El
 * backend expone GET /:clientSlug/:externalUserId, no /:id. Por eso primero
 * buscamos en la lista cacheada por queryKey 'conversations' la conversación
 * matching `id` y de ahí sacamos clientSlug + externalUserId.
 *
 * Si no está cacheada, devolvemos null para que la página muestre estado
 * "selecciona una conversación" en lugar de error.
 */
/**
 * Resolver interno: dado un conversation id (uuid), busca en /conversations
 * por cada clientSlug del user hasta encontrar la conv. Devuelve detail con
 * mensajes ya cargados, o null si no existe.
 */
interface ConversationDetail {
  apiConv: ApiConversation
  messages: ApiIncomingMessage[]
  humanReplies: ApiAuditHumanReply[]
}

async function loadConversationDetail(
  id: string,
  clientSlugs: ClientSlug[],
): Promise<ConversationDetail | null> {
  for (const slug of clientSlugs) {
    try {
      const list = await apiGet<{ items: ApiConversation[] }>(
        `/conversations?clientSlug=${slug}&limit=200`,
      )
      const found = list.items.find((c) => c.id === id)
      if (!found) continue
      const detail = await apiGet<{
        conversation: ApiConversation
        messages: ApiIncomingMessage[]
        humanReplies?: ApiAuditHumanReply[]
      }>(`/conversations/${found.clientSlug}/${encodeURIComponent(found.userExternalId)}`)
      return {
        apiConv: detail.conversation,
        messages: detail.messages,
        humanReplies: detail.humanReplies ?? [],
      }
    } catch {
      continue
    }
  }
  return null
}

// Query única para el detalle — antes useConversation y useMessages cargaban
// por separado el mismo endpoint, lo que duplicaba 2×N fetches (uno por cada
// clientSlug del user). Ahora la query 'conversation-detail' carga una vez
// y los hooks derivan via `select`.
function useConversationDetail(id: string | undefined) {
  const { data: user } = useCurrentUser()
  return useQuery<ConversationDetail | null>({
    queryKey: ['conversation-detail', id],
    enabled: !!id && !!user,
    queryFn: async () => {
      if (!id || !user) return null
      return loadConversationDetail(id, user.clientAccess)
    },
  })
}

export function useConversation(id: string | undefined) {
  const detail = useConversationDetail(id)
  return {
    ...detail,
    data: detail.data
      ? apiConversationToConversation(
          detail.data.apiConv,
          detail.data.messages[detail.data.messages.length - 1]?.body,
        )
      : detail.data === null
        ? null
        : undefined,
  }
}

export function useMessages(conversationId: string | undefined) {
  const detail = useConversationDetail(conversationId)
  return {
    ...detail,
    data: detail.data
      ? buildThread(detail.data, conversationId ?? '')
      : undefined,
  }
}

/**
 * Arma el thread combinando los mensajes entrantes (con su respuesta guardada
 * en la fila) + las respuestas humanas reconstruidas del audit log, ordenados
 * cronológicamente. Dedup: la primera respuesta de un takeover queda tanto en
 * la fila del incoming como en el audit — se muestra una sola vez (misma body +
 * timestamp cercano).
 */
function buildThread(detail: ConversationDetail, conversationId: string): Message[] {
  const incoming = detail.messages.flatMap(apiMessageToFrontend)

  // Respuestas humanas que ya están representadas por la fila del incoming
  // (responseBody de un humano) — para no duplicar.
  const humanOut = incoming.filter(
    (m) => m.direction === 'out' && m.origin === 'human',
  )

  const auditMsgs = detail.humanReplies
    .filter((e) => {
      const body = e.payload?.body ?? ''
      const t = new Date(e.createdAt).getTime()
      // Es duplicado si hay una respuesta humana de la fila con misma body y
      // timestamp cercano (mismo envío).
      const dup = humanOut.some(
        (m) =>
          m.body === body &&
          Math.abs(new Date(m.createdAt).getTime() - t) < 10_000,
      )
      return !dup
    })
    .map((e) => auditReplyToMessage(e, conversationId))

  return [...incoming, ...auditMsgs].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  )
}

export function useUserContext(_conversationId: string | undefined) {
  // Sin tabla de user profiles cross-canal todavía. Devolvemos null hasta
  // implementar Semana 7+ (identidad unificada).
  return useQuery<UserContext | null>({
    queryKey: ['userContext', _conversationId],
    enabled: false,
    queryFn: async () => null,
  })
}
